import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const getInitialUser = () => {
    const localUser = localStorage.getItem('user');
    if (localUser) return JSON.parse(localUser);
    const sessionUser = sessionStorage.getItem('user');
    return sessionUser ? JSON.parse(sessionUser) : null;
  };

  const getInitialToken = () => {
    return localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token') || null;
  };

  const [user, setUser] = useState(getInitialUser);
  const location = useLocation();
  
  // Public route kontrolü - memoize et
  const isPublicRoute = useMemo(() => {
    const publicRoutes = ['/pastel', '/cari/', '/login'];
    return publicRoutes.some(route => {
      if (route === '/cari/') {
        return location.pathname.startsWith('/cari/');
      }
      return location.pathname === route || location.pathname.startsWith(route);
    });
  }, [location.pathname]);
  
  // Public route'larda loading'i başlangıçta false yap
  const [loading, setLoading] = useState(() => {
    const publicRoutes = ['/pastel', '/cari/', '/login'];
    const currentPath = window.location.pathname;
    const isPublic = publicRoutes.some(route => {
      if (route === '/cari/') {
        return currentPath.startsWith('/cari/');
      }
      return currentPath === route || currentPath.startsWith(route);
    });
    return !isPublic; // Public route'larda false, değilse true
  });
  const [token, setToken] = useState(getInitialToken);
  const refreshTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const navigate = useNavigate();

  const clearHeartbeatTimer = () => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  };

  const startPresenceHeartbeat = () => {
    clearHeartbeatTimer();
    const sendHeartbeat = async () => {
      try {
        await api.post('/api/auth/heartbeat');
      } catch (_) {
        // No-op: heartbeat failure should not block user flow
      }
    };

    sendHeartbeat();
    heartbeatTimerRef.current = setInterval(sendHeartbeat, 10 * 60 * 1000);
  };

  const getActiveStorage = () => {
    if (localStorage.getItem('jansoft-token')) return localStorage;
    if (sessionStorage.getItem('jansoft-token')) return sessionStorage;
    return localStorage;
  };

  const persistUser = (updatedUser) => {
    if (!updatedUser) return;
    const storage = getActiveStorage();
    storage.setItem('user', JSON.stringify(updatedUser));
  };

  // Token değiştiğinde Authorization header'ı ayarla
  useEffect(() => {
    if (token) {
      // api instance'ı zaten interceptor'da token'ı ekliyor
      // Burada ek bir şey yapmaya gerek yok
    }
  }, [token]);

  // Public route değiştiğinde loading'i ayarla
  useEffect(() => {
    if (isPublicRoute) {
      setLoading(false);
    }
  }, [isPublicRoute]);

  // Check if user is authenticated on mount
  useEffect(() => {
    // Public route'larda token kontrolü yapma ve loading'i false yap
    if (isPublicRoute) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await api.get('/api/auth/me');
        setUser(response.data.user);
        // Persist user alongside the token storage location
        const activeStorage = localStorage.getItem('jansoft-token') === token
          ? localStorage
          : sessionStorage.getItem('jansoft-token') === token
            ? sessionStorage
            : localStorage;
        activeStorage.setItem('user', JSON.stringify(response.data.user));
        scheduleProactiveRefresh(token);
        startPresenceHeartbeat();
      } catch (error) {
        // Token geçersizse sadece temizle, logout çağırma (çünkü zaten public route değilse ProtectedRoute redirect edecek)
        setUser(null);
        setToken(null);
        localStorage.removeItem('jansoft-token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('jansoft-token');
        sessionStorage.removeItem('user');
      }
      setLoading(false);
    };

    checkAuth();
    return () => {
      clearHeartbeatTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isPublicRoute]);

  const login = async (credentials, options = { keepSignedIn: true }) => {
    try {
      setLoading(true);
      const response = await api.post('/api/auth/login', credentials);
      
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      const storage = options.keepSignedIn ? localStorage : sessionStorage;
      storage.setItem('jansoft-token', newToken);
      storage.setItem('user', JSON.stringify(userData));
      // Ensure no stale data remains in the other storage
      const other = options.keepSignedIn ? sessionStorage : localStorage;
      other.removeItem('jansoft-token');
      other.removeItem('user');
      scheduleProactiveRefresh(newToken);
      startPresenceHeartbeat();
      
      navigate('/');
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      const activeStorage = getActiveStorage();
      const currentToken = activeStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token') || localStorage.getItem('jansoft-token');
      if (!currentToken) throw new Error('No token in storage');
      const resp = await api.post('/api/auth/refresh', { token: currentToken });
      const newToken = resp.data?.token;
      if (!newToken) throw new Error('No token in refresh response');
      activeStorage.setItem('jansoft-token', newToken);
      setToken(newToken);
      scheduleProactiveRefresh(newToken);
      return newToken;
    } catch (err) {
      // On failure, fall back to logout which also clears timers
      logout();
      throw err;
    }
  };

  const scheduleProactiveRefresh = (jwtToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (!jwtToken) return;
    try {
      const [, payloadB64] = jwtToken.split('.');
      const payloadJson = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);
      const expMs = (payload.exp || 0) * 1000;
      const thresholdMs = 5 * 60 * 1000; // 5 dakika önce
      const delay = expMs - Date.now() - thresholdMs;
      if (delay > 0) {
        refreshTimerRef.current = setTimeout(() => {
          refreshToken().catch(() => {});
        }, delay);
      }
    } catch (_) {
      // Eğer decode edilemezse zamanlayıcı kurmayalım
    }
  };

  const logout = (skipRedirect = false) => {
    setUser(null);
    setToken(null);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    clearHeartbeatTimer();
    localStorage.removeItem('jansoft-token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('jansoft-token');
    sessionStorage.removeItem('user');
    
    // Public route'larda veya skipRedirect true ise login'e yönlendirme yapma
    if (!skipRedirect && !isPublicRoute) {
      navigate('/login');
      toast.success('Çıkış yapıldı');
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await api.post('/api/auth/register', userData);
      toast.success('Kayıt başarılı! Giriş yapabilirsiniz.');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Kayıt yapılamadı';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/api/users/profile', profileData);
      setUser(response.data.user);
      toast.success('Profil güncellendi!');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Profil güncellenemedi';
      toast.error(message);
      throw error;
    }
  };

  const updateUserData = (patch = {}) => {
    setUser(prev => {
      const next = prev ? { ...prev, ...patch } : { ...patch };
      persistUser(next);
      return next;
    });
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    refreshToken,
    register,
    updateProfile,
    updateUserData,
    isAuthenticated: !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
